import { useQueryClient } from '@tanstack/react-query';

import { useApiClient, useToastService } from '@@common';

import { ApiKeyModel, EnvironmentClient, EnvironmentModel, ReleasePlanCreatedResult } from '../../../api/api';

type ApiKeyDeploymentOperation = 'addApiKey' | 'redeployApiKey';

interface UseEnvironmentApiKeyActionsOptions {
	onReleasePlanCreated?: (releasePlanId: string, environment: EnvironmentModel) => void;
	onAfterAction?: () => void | Promise<void>;
}

export default function useEnvironmentApiKeyActions({ onReleasePlanCreated, onAfterAction }: UseEnvironmentApiKeyActionsOptions = {}) {
	const environmentClient = useApiClient(EnvironmentClient) as EnvironmentClient;
	const toastService = useToastService();
	const queryClient = useQueryClient();

	const refresh = async () => {
		await queryClient.invalidateQueries({ queryKey: ['environments'] });
		await queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
		await onAfterAction?.();
	};

	const handleReleasePlan = (result: ReleasePlanCreatedResult, environment: EnvironmentModel) => {
		if (result.releasePlanId) {
			onReleasePlanCreated?.(result.releasePlanId, environment);
		}
	};

	const deployApiKey = async (environment: EnvironmentModel, apiKey: ApiKeyModel, operation: ApiKeyDeploymentOperation = 'addApiKey') => {
		try {
			const result = await environmentClient.addEnvironmentApiKey(environment.id, { apiKeyId: apiKey.id });
			toastService.success(`API key "${apiKey.name}" ${operation === 'redeployApiKey' ? 'redeploy' : 'deployment'} queued for ${environment.name}.`);
			await refresh();
			handleReleasePlan(result, environment);
			return result;
		} catch (error) {
			console.error('Error deploying API key:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to deploy API key: ${errorMessage}`);
			throw error;
		}
	};

	const removeApiKey = async (environment: EnvironmentModel, apiKey: ApiKeyModel) => {
		try {
			const result = await environmentClient.removeEnvironmentApiKey(environment.id, apiKey.id);
			toastService.success(`API key "${apiKey.name}" removal queued for ${environment.name}.`);
			await refresh();
			handleReleasePlan(result, environment);
			return result;
		} catch (error) {
			console.error('Error removing API key from environment:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove API key: ${errorMessage}`);
			throw error;
		}
	};

	const rotateApiKeySecret = async (environment: EnvironmentModel, apiKey: ApiKeyModel) => {
		try {
			const result = await environmentClient.rotateEnvironmentApiKeySecret(environment.id, apiKey.id);
			toastService.success(`API key "${apiKey.name}" secret rotation queued for ${environment.name}.`);
			await refresh();
			handleReleasePlan(result, environment);
			return result;
		} catch (error) {
			console.error('Error rotating environment API key secret:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to rotate API key secret: ${errorMessage}`);
			throw error;
		}
	};

	return {
		deployApiKey,
		removeApiKey,
		rotateApiKeySecret,
		refreshEnvironmentApiKeys: refresh,
	};
}
